import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import LargeTextField from "../LargeTextField";
import LabeledTextField from "../LabeledTextField";
import SectionHeader from "../SectionHeader";
import { sdmTableStyles, getHeaderCellStyle } from "../sdmTableStyles";

const FINANCIAL_POSITION_OPTIONS = [
  "хорошее",
  "среднее-1",
  "среднее-2",
  "среднее-3",
  "среднее-4",
  "среднее-5",
  "среднее-6",
  "плохое-1",
  "плохое-2",
  "плохое-3",
];

const RKKCommentsTab = ({ data, onUpdate }) => {
  const comments = data.comments || {};
  const creditTargets = comments.creditTargets || [];

  const handleTargetChange = (index, field, value) => {
    const updated = creditTargets.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    onUpdate("comments.creditTargets", updated);
  };

  const handleAddRow = () => {
    onUpdate("comments.creditTargets", [
      ...creditTargets,
      { contractNumber: "", purpose: "", repaymentSource: "" },
    ]);
  };

  const handleDeleteRow = (index) => {
    onUpdate(
      "comments.creditTargets",
      creditTargets.filter((_, i) => i !== index)
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0px", paddingBottom: "20px" }}>
      <SectionHeader variant="secondary">Комментарии к отчетности компании</SectionHeader>

      <LargeTextField
        label="Комментарии к балансу, коэффициентам, основным изменениям и динамике:"
        value={comments.balanceComment || ""}
        onChange={(v) => onUpdate("comments.balanceComment", v)}
        minRows={4}
      />

      <LargeTextField
        label="Комментарии к показателям ОПиУ, коэффициентам, их изменениям и динамике:"
        value={comments.pnLComment || ""}
        onChange={(v) => onUpdate("comments.pnLComment", v)}
        minRows={4}
      />

      <SectionHeader variant="secondary">Комментарии к другим факторам, влияющим на фин.положение компании:</SectionHeader>

      <LabeledTextField
        label="Просроченная дебиторка:"
        value={comments.overdueReceivables || ""}
        onChange={(v) => onUpdate("comments.overdueReceivables", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Неликвидные активы:"
        value={comments.illiquidAssets || ""}
        onChange={(v) => onUpdate("comments.illiquidAssets", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Судебные требования от компании:"
        value={comments.courtClaimsFromCompany || ""}
        onChange={(v) => onUpdate("comments.courtClaimsFromCompany", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Судебные требования к компании:"
        value={comments.courtClaimsToCompany || ""}
        onChange={(v) => onUpdate("comments.courtClaimsToCompany", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Просрочена выплата зарплаты:"
        value={comments.overdueSalary || ""}
        onChange={(v) => onUpdate("comments.overdueSalary", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Просрочены платежи в бюджет, картотека:"
        value={comments.overdueTaxPayments || ""}
        onChange={(v) => onUpdate("comments.overdueTaxPayments", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Просрочки по кредитам банков:"
        value={comments.overdueBankLoans || ""}
        onChange={(v) => onUpdate("comments.overdueBankLoans", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Просроченная кредиторка:"
        value={comments.overduePayables || ""}
        onChange={(v) => onUpdate("comments.overduePayables", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Отрицательный собственный капитал:"
        value={comments.negativeEquity || ""}
        onChange={(v) => onUpdate("comments.negativeEquity", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Отличия чистой прибыли и изменения нераспределённой прибыли в балансе:"
        value={comments.netProfitRetainedEarningsMismatch || ""}
        onChange={(v) => onUpdate("comments.netProfitRetainedEarningsMismatch", v)}
        labelWidth="320px"
      />
      <LabeledTextField
        label="Недостоверность отчётности:"
        value={comments.unreliabilityOfReport || ""}
        onChange={(v) => onUpdate("comments.unreliabilityOfReport", v)}
        labelWidth="320px"
      />



      <Box sx={sdmTableStyles.tableWrapper}>
        <Box sx={sdmTableStyles.headerSection}>
          <Typography sx={sdmTableStyles.sectionTitle}>
            Целевое использование и источники погашения кредита
          </Typography>
        </Box>
        <TableContainer sx={sdmTableStyles.tableContainer}>
          <Table size={sdmTableStyles.tableSize}>
            <TableHead>
              <TableRow sx={sdmTableStyles.headerRow}>
                <TableCell sx={getHeaderCellStyle(160)}>№ договора</TableCell>
                <TableCell sx={getHeaderCellStyle(260)}>Целевое использование</TableCell>
                <TableCell sx={getHeaderCellStyle(260)}>Источник погашения</TableCell>
                <TableCell sx={{ ...sdmTableStyles.headerCell, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {creditTargets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={sdmTableStyles.emptyStateRow}>
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
              {creditTargets.map((row, index) => (
                <TableRow key={index} sx={sdmTableStyles.dataRow}>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      value={row.contractNumber || ""}
                      onChange={(e) => handleTargetChange(index, "contractNumber", e.target.value)}
                      fullWidth
                      size="small"
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      value={row.purpose || ""}
                      onChange={(e) => handleTargetChange(index, "purpose", e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      sx={sdmTableStyles.textFieldMultiline}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      value={row.repaymentSource || ""}
                      onChange={(e) => handleTargetChange(index, "repaymentSource", e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      sx={sdmTableStyles.textFieldMultiline}
                    />
                  </TableCell>
                  <TableCell sx={{ ...sdmTableStyles.dataCell, width: 48 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRow(index)}
                      sx={sdmTableStyles.deleteButton}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={4} sx={sdmTableStyles.addRowCell}>
                  <Button
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={handleAddRow}
                    sx={sdmTableStyles.addRowButton}
                  >
                    Добавить строку
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <LargeTextField
        label="Вывод по оценке финансового положения:"
        value={comments.financialPositionConclusion || ""}
        onChange={(v) => onUpdate("comments.financialPositionConclusion", v)}
        minRows={3}
      />

      <Box>
        <FormControl size="small" fullWidth>
          <Select
            value={comments.financialPositionCategory || ""}
            onChange={(e) => onUpdate("comments.financialPositionCategory", e.target.value)}
            displayEmpty
            sx={{
              fontSize: "14px",
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#1976d2" },
              "&.Mui-focused fieldset": { borderColor: "#1976d2" },
            }}
          >
            <MenuItem value=""><em>Не выбрано</em></MenuItem>
            {FINANCIAL_POSITION_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default RKKCommentsTab;
